import { useEffect, useState } from "react";
import { LogOut, RefreshCw, Wrench } from "lucide-react";
import { toast } from "sonner";
import {
  adminLogin,
  adminLogout,
  fetchAdminOrders,
  formatNZD,
  updateOrderStatus,
} from "@/lib/api";

const STATUSES = ["new", "invoiced", "paid", "in-build", "ready", "completed"];

const STATUS_STYLE = {
  new: "bg-volt/10 text-volt border-volt/30",
  invoiced: "bg-amber-100 text-amber-700 border-amber-300",
  paid: "bg-emerald-100 text-emerald-700 border-emerald-300",
  "in-build": "bg-blue-100 text-blue-700 border-blue-300",
  ready: "bg-violet-100 text-violet-700 border-violet-300",
  completed: "bg-zinc-200 text-zinc-600 border-zinc-300",
};

const DELIVERY_LABEL = {
  ship: "Ship to door",
  auckland: "Auckland pickup / drop-off",
  installation: "Auckland installation",
};

export default function Admin() {
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = () =>
    fetchAdminOrders()
      .then((data) => {
        setOrders(data);
        setLoaded(true);
        setAuthed(true);
      })
      .catch(() => setAuthed(false));

  useEffect(() => {
    load().finally(() => setChecking(false));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      await adminLogin(email, password);
      await load();
      toast.success("Welcome back");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoggingIn(false);
      setPassword("");
    }
  };

  const handleStatus = async (orderId, status) => {
    try {
      await updateOrderStatus(orderId, status);
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status } : o)),
      );
      toast.success(`${orderId} → ${status}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update status");
    }
  };

  const handleLogout = async () => {
    await adminLogout().catch(() => {});
    setAuthed(false);
    setLoaded(false);
    setOrders([]);
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface text-sm text-zinc-500">
        Checking session…
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface px-5">
        <form
          onSubmit={handleLogin}
          className="w-full max-w-sm rounded-2xl border border-black/10 bg-white p-8"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-volt" />
            <p className="eyebrow">Owner Dashboard</p>
          </div>
          <h1 className="mt-3 font-display text-2xl font-extrabold text-ink">
            Sign in to see your orders.
          </h1>
          <label className="mt-6 block">
            <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
              Email
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="admin-login-email"
              className="w-full rounded-[3px] border border-black/20 bg-white px-4 py-3 text-sm text-ink focus:border-volt focus:outline-none"
            />
          </label>
          <label className="mt-4 block">
            <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
              Password
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-login-password"
              className="w-full rounded-[3px] border border-black/20 bg-white px-4 py-3 text-sm text-ink focus:border-volt focus:outline-none"
            />
          </label>
          <button
            type="submit"
            disabled={loggingIn}
            data-testid="admin-login-submit"
            className="mt-6 w-full rounded-[3px] bg-volt px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-volt-dim disabled:opacity-60"
          >
            {loggingIn ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    );
  }

  const newCount = orders.filter((o) => o.status === "new").length;
  const paidTotal = orders
    .filter((o) => ["paid", "in-build", "ready", "completed"].includes(o.status))
    .reduce((n, o) => n + o.total, 0);

  return (
    <div className="min-h-screen bg-surface">
      <header className="border-b border-black/10 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-5 sm:px-8">
          <div>
            <p className="eyebrow">AKL E-KITS</p>
            <h1 className="font-display text-xl font-extrabold text-ink">
              Order Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 rounded-[3px] border border-black/15 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 transition-colors duration-200 hover:border-volt hover:text-volt"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-[3px] border border-black/15 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 transition-colors duration-200 hover:border-red-400 hover:text-red-500"
            >
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { label: "Total orders", value: orders.length, testid: "admin-stat-total" },
            { label: "New (needs action)", value: newCount, testid: "admin-stat-new" },
            { label: "Paid & beyond", value: formatNZD(paidTotal), testid: "admin-stat-paid" },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-black/10 bg-white p-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{s.label}</p>
              <p className="mt-2 font-display text-3xl font-extrabold text-ink" data-testid={s.testid}>
                {s.value}
              </p>
            </div>
          ))}
        </div>

        {!loaded ? (
          <p className="mt-10 text-sm text-zinc-500">Loading orders…</p>
        ) : orders.length === 0 ? (
          <p className="mt-10 text-sm text-zinc-500" data-testid="admin-orders-empty">
            No orders yet — they'll land here the moment one is placed.
          </p>
        ) : (
          <ul className="mt-8 space-y-4" data-testid="admin-orders-list">
            {orders.map((o) => (
              <li
                key={o.id}
                className="rounded-xl border border-black/10 bg-white p-5 sm:p-6"
                data-testid={`admin-order-${o.id}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-mono text-lg font-bold text-volt">{o.order_number}</p>
                    <p className="mt-0.5 font-mono text-[11px] text-zinc-500">
                      {new Date(o.created_at).toLocaleString("en-NZ")}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${STATUS_STYLE[o.status] || "border-zinc-300 text-zinc-500"}`}
                      data-testid={`admin-order-${o.id}-status-chip`}
                    >
                      {o.status}
                    </span>
                    <select
                      value={o.status}
                      onChange={(e) => handleStatus(o.id, e.target.value)}
                      data-testid={`admin-order-${o.id}-status`}
                      className="rounded-[3px] border border-black/20 bg-white px-3 py-2 text-xs text-ink focus:border-volt focus:outline-none"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-4 grid gap-4 text-sm sm:grid-cols-3">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Customer</p>
                    <p className="mt-1 font-bold text-ink">{o.customer.name}</p>
                    <p className="text-zinc-600">{o.customer.email}</p>
                    {o.customer.phone && <p className="text-zinc-600">{o.customer.phone}</p>}
                  </div>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Delivery</p>
                    <p className="mt-1 font-bold text-ink">{DELIVERY_LABEL[o.delivery] || o.delivery}</p>
                    {o.delivery === "installation" && (o.dropoff_date || o.dropoff_time) && (
                      <p className="mt-0.5 rounded bg-volt/10 px-2 py-1 text-xs font-bold text-volt" data-testid={`admin-order-${o.id}-dropoff`}>
                        Drop-off: {[o.dropoff_date, o.dropoff_time].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    <p className="mt-1 text-zinc-600">
                      {[o.shipping_address.street, o.shipping_address.suburb, o.shipping_address.city].filter(Boolean).join(", ")}
                    </p>
                  </div>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Total</p>
                    <p className="mt-1 font-mono text-lg font-bold text-ink">
                      {formatNZD(o.total)} {o.currency}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {o.installation_fee
                        ? `incl. ${formatNZD(o.installation_fee)} installation`
                        : `incl. ${formatNZD(o.shipping)} delivery${o.discount_code ? ` (${o.discount_code} ${o.discount_pct}% off)` : ""}`}
                    </p>
                  </div>
                </div>

                <ul className="mt-4 border-t border-black/10 pt-3 text-sm text-zinc-700">
                  {o.items.map((it, i) => (
                    <li key={i} className="flex justify-between gap-4 py-1">
                      <span>
                        {it.name} × {it.qty}
                        <span className="block text-xs text-zinc-500">
                          {it.detail_lines.map((l) => l.name).join(" · ")}
                        </span>
                      </span>
                      <span className="shrink-0 font-mono">{formatNZD(it.line_total)}</span>
                    </li>
                  ))}
                </ul>
                {o.notes && (
                  <p className="mt-3 rounded-lg bg-surface p-3 text-xs text-zinc-600">
                    <span className="font-bold text-ink">Notes:</span> {o.notes}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
